/**
 * The lead drawer's "Quotable check" block: WhatConverts' mark, every
 * labeler's answer with its reason, the team's correction control, and the
 * full correction history (newest first).
 *
 * Only rendered when lead labels are on, so the drawer works as before
 * without them. Everything here stays in Core.
 */
import { useQuery } from '@tanstack/react-query';

import { Badge } from '@/components/ui/badge';
import {
  getLeadLabelDetail,
  type WcLead,
  type WcLeadLabel,
} from '@/services/whatconvertsService';

import { CorrectionControl, TeamMarker } from './LeadLabelParts';
import { AI_LABELER, LABEL_BADGE, LABEL_TEXT, labelerName } from './leadLabels';
import { OutsideChangesList } from './outsideChanges';
import { writebackSummary } from './quotableWriteback';

const WC_TEXT = { ...LABEL_TEXT, not_set: 'Not set' } as const;

function when(iso: string | null | undefined): string {
  if (!iso) return '';
  const t = new Date(iso);
  return Number.isNaN(t.getTime())
    ? iso
    : t.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function LabelerRow({ lead, label }: { lead: WcLead; label: WcLeadLabel }) {
  return (
    <div className="space-y-0.5">
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="text-muted-foreground">{labelerName(label.labeler)}</span>
        <span className="flex items-center gap-1.5">
          <TeamMarker lead={lead} answer={label.label} />
          <Badge variant="outline" className={LABEL_BADGE[label.label]}>
            {LABEL_TEXT[label.label]}
          </Badge>
        </span>
      </div>
      {label.reason ? <p className="text-xs text-muted-foreground">{label.reason}</p> : null}
    </div>
  );
}

export function LeadLabelsPanel({ lead, tenantId }: { lead: WcLead; tenantId: string }) {
  const leadId = String(lead.lead_id);
  const detail = useQuery({
    queryKey: ['wc-lead-label-detail', tenantId, leadId],
    queryFn: () => getLeadLabelDetail(tenantId, leadId),
    retry: 0,
    refetchOnWindowFocus: false,
  });
  // Newest label per labeler from the table row; the AI first.
  const labels = [...(lead.labels ?? [])].sort((a, b) =>
    a.labeler === AI_LABELER ? -1 : b.labeler === AI_LABELER ? 1 : a.labeler.localeCompare(b.labeler),
  );
  const history = detail.data?.corrections ?? [];
  const writeback = writebackSummary(detail.data?.writeback);

  return (
    <div className="rounded-md border border-border bg-warm-bg p-3" data-testid="lead-labels-panel">
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        Quotable check
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2 text-sm">
          <span className="text-muted-foreground">WhatConverts</span>
          <span className="flex items-center gap-1.5">
            <TeamMarker lead={lead} answer={lead.quotable === 'not_set' ? null : lead.quotable} />
            <Badge variant="outline" className={LABEL_BADGE[lead.quotable] ?? LABEL_BADGE.not_set}>
              {WC_TEXT[lead.quotable] ?? lead.quotable}
            </Badge>
          </span>
        </div>
        {labels.length ? (
          labels.map((l) => <LabelerRow key={l.labeler} lead={lead} label={l} />)
        ) : (
          <p className="text-xs text-muted-foreground">Core AI has not labelled this lead yet.</p>
        )}
      </div>

      {writeback ? (
        <div className="mt-3 border-t border-border pt-3" data-testid="lead-writeback">
          <div className="mb-1 text-sm font-medium">Written to WhatConverts</div>
          <p className="text-xs text-muted-foreground">{writeback.line}</p>
        </div>
      ) : null}

      <OutsideChangesList changes={detail.data?.outside_changes} />

      <div className="mt-3 border-t border-border pt-3">
        <div className="mb-1.5 text-sm font-medium">Team check</div>
        <CorrectionControl lead={lead} tenantId={tenantId} />
      </div>

      <div className="mt-3 border-t border-border pt-3">
        <div className="mb-1.5 text-sm font-medium">Correction history</div>
        {detail.isLoading ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : detail.isError ? (
          <p className="text-xs text-muted-foreground">The history could not be loaded.</p>
        ) : history.length === 0 ? (
          <p className="text-xs text-muted-foreground">Nobody has corrected this lead yet.</p>
        ) : (
          <ol className="space-y-1.5" data-testid="correction-history">
            {history.map((c, i) => (
              <li key={`${c.created_at}-${i}`} className="text-xs">
                <span className="font-medium text-foreground">{LABEL_TEXT[c.label]}</span>
                <span className="text-muted-foreground">
                  {' '}by {c.actor_email}
                  {c.created_at ? `, ${when(c.created_at)}` : ''}
                  {i === 0 ? ' (current)' : ''}
                </span>
                {c.note ? <div className="text-muted-foreground">{c.note}</div> : null}
              </li>
            ))}
          </ol>
        )}
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        {writeback
          ? writeback.footer
          : 'Kept in Core only. Nothing here changes WhatConverts or Google Ads.'}
      </p>
    </div>
  );
}
